import { Component } from './component-base.js';

const SCROLL_DISTANCE_PX = 500;

function clamp01(n) {
    return Math.min(1, Math.max(0, n));
}

/** Header renders the same logo twice (mobile / desktop rows); only one has a non-zero layout box. */
function findVisibleHeaderFloatingLogo() {
    for (const el of document.querySelectorAll('[data-header-floating-logo="true"]')) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return el;
    }
    return document.querySelector('[data-header-floating-logo="true"]');
}

class ShrinkOnScroll extends Component {
    #ticking = false;
    #scrollReleased = false;
    #isClamping = false;
    #siteHeaderSection = null;
    #floatingLogo = null;
    #headerLogo = null;
    #scrollBudgetPx = SCROLL_DISTANCE_PX;
    /** Immutable { left, top, width, height } — never a live DOMRect (WebKit can make geometry reads unstable). */
    #floatingStartRect = null;
    #logoResizeObserver = null;
    #reduceMotion = false;
    #headerLogoWidthDesktop = null;
    #headerLogoWidthMobile = null;

    /** Bumps each time we snapshot the hero logo box; layout insets re-apply only when this changes. */
    #startRectEpoch = 0;
    /** Last `#startRectEpoch` we applied fixed left/top/width/height for. */
    #appliedLayoutEpoch = -1;

    /** Used to ignore iOS `resize` when only the visual viewport height changes (URL bar) — same `innerWidth`. */
    #lastResizeInnerWidth = 0;

    #captureFloatingStartRect() {
        if (!this.#floatingLogo || this.#reduceMotion) return;
        /** One snapshot per “session” until resize/null — avoids connect rAF overwriting after scroll. */
        if (this.#floatingStartRect != null) return;
        const r = this.#floatingLogo.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) return;
        this.#floatingStartRect = {
            left: r.left,
            top: r.top,
            width: r.width,
            height: r.height,
        };
        this.#startRectEpoch += 1;
    }

    #lerp(a, b, t) {
        return a + (b - a) * t;
    }

    #ensureHeaderLogoRef() {
        const next = findVisibleHeaderFloatingLogo();
        if (next === this.#headerLogo) return this.#headerLogo;

        this.#logoResizeObserver?.disconnect();
        this.#headerLogo = next;

        if (this.#headerLogo) {
            this.#headerLogoWidthDesktop =
                Number(this.#headerLogo.dataset.logoWidthDesktop) || 0;
            this.#headerLogoWidthMobile =
                Number(this.#headerLogo.dataset.logoWidthMobile) || 0;
            if (!this.#logoResizeObserver) {
                this.#logoResizeObserver = new ResizeObserver(() =>
                    this.#onHeaderResize()
                );
            }
            this.#logoResizeObserver.observe(this.#headerLogo);
        }
        return this.#headerLogo;
    }

    #updateFloatingLogo(progress) {
        const floating = this.#floatingLogo;
        if (!floating || this.#reduceMotion) return;

        let header = this.#headerLogo;
        if (!header || header.getBoundingClientRect().width <= 0) {
            header = this.#ensureHeaderLogoRef();
        }
        if (!header) return;

        if (!this.#floatingStartRect) {
            this.#captureFloatingStartRect();
        }

        const start = this.#floatingStartRect;
        if (!start || start.width <= 0 || start.height <= 0) return;

        const end = header.getBoundingClientRect();
        const p = progress;

        let w = this.#lerp(start.width, end.width, p);
        let h = this.#lerp(start.height, end.height, p);

        const startCx = start.left + start.width / 2;
        const startCy = start.top + start.height / 2;
        const endCx = end.left + end.width / 2;
        const endCy = end.top + end.height / 2;
        const cx = this.#lerp(startCx, endCx, p);
        let cy = this.#lerp(startCy, endCy, p);
        const cyLo = Math.min(startCy, endCy);
        const cyHi = Math.max(startCy, endCy);
        cy = Math.min(Math.max(cy, cyLo), cyHi);
        let left = cx - w / 2;
        let top = cy - h / 2;

        const targetTop = endCy - h / 2;
        top = Math.max(top, targetTop, end.top);

        if (p >= 0.995) {
            left = end.left;
            top = end.top;
            w = end.width;
            h = end.height;
        }

        const fadeStart = 0.99;
        const fadeT = p <= fadeStart ? 0 : (p - fadeStart) / (1 - fadeStart);
        const headerOpacity = fadeT;

        if (p >= 0.999) {
            this.#appliedLayoutEpoch = -1;
            floating.style.cssText = '';
            floating.setAttribute('aria-hidden', 'true');
            floating.style.visibility = "hidden";
            header.style.opacity = '';
            header.style.pointerEvents = '';
            return;
        }

        floating.removeAttribute('aria-hidden');
        floating.style.visibility = '';

        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const minLogoWidth = isDesktop
            ? this.#headerLogoWidthDesktop
            : this.#headerLogoWidthMobile;
        /** Matches previous width + minWidth behavior: used width was max(w, minLogoWidth). */
        const effW = Math.max(w, minLogoWidth);
        const effH = h;
        const sx = effW / start.width;
        const sy = effH / start.height;
        const targetCx = left + effW / 2;
        const targetCy = top + effH / 2;
        const dx = targetCx - startCx;
        const dy = targetCy - startCy;

        if (this.#appliedLayoutEpoch !== this.#startRectEpoch) {
            this.#appliedLayoutEpoch = this.#startRectEpoch;
            Object.assign(floating.style, {
                position: 'fixed',
                left: `${start.left}px`,
                top: `${start.top}px`,
                width: `${start.width}px`,
                height: `${start.height}px`,
                margin: '0',
                transformOrigin: '50% 50%',
                zIndex: '30',
                pointerEvents: 'none',
                boxSizing: 'border-box',
                willChange: 'transform',
            });
        }

        floating.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${sx}, ${sy})`;
        floating.style.filter = `invert(${p})`;

        header.style.opacity = String(headerOpacity);
        header.style.pointerEvents = headerOpacity < 0.5 ? 'none' : '';
    }

    /** Single `progress` (0→1 over `#scrollBudgetPx` scroll) drives inset, CSS var, and floating logo. */
    #apply(progress) {
        this.style.setProperty('--media-banner-shrink-progress', String(progress));
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const maxInset = isDesktop
            ? this.maxInset
            : this.maxInsetMobile;
        const insetPx = progress * maxInset;
        this.style.setProperty('--media-banner-shrink-inset', `${insetPx}px`);
        this.closest('.shopify-section')?.style.setProperty(
            '--media-banner-shrink-inset',
            `${insetPx}px`
        );
        this.#siteHeaderSection?.classList.toggle('shunk-alternative', progress >= 0.38);
        this.classList.toggle('is-shrunk', progress >= 0.99);

        if (progress >= 0.99) {
            this.#scrollReleased = true;
        }
        this.#updateFloatingLogo(progress);
    }

    #clampScrollPosition() {
        if (this.#scrollReleased || this.#isClamping) return;
        const y = window.scrollY;
        if (y <= this.#scrollBudgetPx) return;
        this.#isClamping = true;
        window.scrollTo({ top: this.#scrollBudgetPx, left: 0, behavior: 'instant'});
        requestAnimationFrame(() => {
            this.#isClamping = false;
        });
    }

    #onScroll = () => {
        if (this.#ticking) return;
        this.#ticking = true;
        requestAnimationFrame(() => {
            this.#ticking = false;
            this.#clampScrollPosition();
            const y = window.scrollY;
            const p = clamp01(y / this.#scrollBudgetPx);
            this.#apply(p);
        });
    };

    #onLogoLayoutChange = () => {
        const w = window.innerWidth;
        const narrowViewport = window.matchMedia('(max-width: 767px)').matches;
        if (narrowViewport && w === this.#lastResizeInnerWidth) {
            this.#ensureHeaderLogoRef();
            const p = clamp01(window.scrollY / this.#scrollBudgetPx);
            this.#updateFloatingLogo(p);
            return;
        }

        this.#lastResizeInnerWidth = w;

        if (this.#floatingLogo) {
            this.#floatingLogo.style.cssText = '';
        }
        this.#floatingStartRect = null;
        this.#appliedLayoutEpoch = -1;
        this.#ensureHeaderLogoRef();
        const p = clamp01(window.scrollY / this.#scrollBudgetPx);
        this.#captureFloatingStartRect();
        this.#updateFloatingLogo(p);
    };

    #onHeaderResize = () => {
        const p = clamp01(window.scrollY / this.#scrollBudgetPx);
        this.#updateFloatingLogo(p);
    };

    connectedCallback() {
        super.connectedCallback();

        if (this.initialised) return;
        this.initialised = true;

        this.maxInset = Number(this.dataset.shrinkOnScrollAmount) || 0;
        this.maxInsetMobile = Number(this.dataset.shrinkOnScrollAmountMobile) || 0;
        this.#siteHeaderSection = document.querySelector('.site-header');
        this.#floatingLogo = document.querySelector('[data-floating-logo="true"]');
        this.#headerLogo = document.querySelector('[data-header-floating-logo="true"]');

        this.#ensureHeaderLogoRef();
        this.#scrollBudgetPx = Number(this.dataset.shrinkScrollBudget) || SCROLL_DISTANCE_PX;
        this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (window.scrollY >= this.#scrollBudgetPx) {
            this.#scrollReleased = true;
        }

        if (this.#floatingLogo && this.#headerLogo && !this.#reduceMotion) {
            requestAnimationFrame(() => this.#captureFloatingStartRect());
        }

        this.#lastResizeInnerWidth = window.innerWidth;

        window.addEventListener('scroll', this.#onScroll, { passive: true });
        this.#onScroll();

        window.addEventListener('resize', this.#onLogoLayoutChange, { passive: true});
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('scroll', this.#onScroll);
        window.removeEventListener('resize', this.#onLogoLayoutChange);
        this.#logoResizeObserver?.disconnect();
        this.#logoResizeObserver = null;
        this.#appliedLayoutEpoch = -1;
    }
}

function initShrinkOnScroll() {
    if (!customElements.get('shrink-on-scroll')) {
        customElements.define('shrink-on-scroll', ShrinkOnScroll);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShrinkOnScroll);
} else {
    initShrinkOnScroll();
}

export { ShrinkOnScroll, initShrinkOnScroll };
